import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

import { getTrans as t } from "../../Localization/translations";
import { API } from "../../backend";
import authHeader from "../../services/auth-header";
import {
    toastOptions,
    errorMessage,
    panelStyle,
    controlStyle,
    buttonStyle,
    headingStyle,
    columns,
} from "./RentMapping";

// Two fields + the small actions column; same size and look as the Rent panel.
const gridStyle = {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) 62px",
    columnGap: 10,
    alignItems: "center",
};

let draftCounter = 0;
const toRow = (code) => ({ key: `saved-${code.id}`, ...code, dirty: false });
const newDraft = () => ({
    key: `draft-${++draftCounter}`,
    id: null,
    fee_type: "",
    tax_code: "",
    dirty: false,
});
// Saved rows, then always one blank row to fill in.
const withBlankRow = (rows) => {
    const last = rows[rows.length - 1];
    return last && !last.id && !last.dirty ? rows : [...rows, newDraft()];
};

// Opens under a product's Fees line on Settings > Products: the Avalara tax code a Fees line takes
// for one fee type. The fee types are the Fee categories from Fees & Expenses Categories (the same
// API the invoice screen uses; expenses are not listed). Used only when Avalara is the tax engine.
// A fee type with no row here uses the Fees tax code above.
const FeeMapping = ({ product, codes, onRowsChange }) => {
    const [rows, setRows] = useState(() => withBlankRow(codes.map(toRow)));
    const [feeTypes, setFeeTypes] = useState([]);

    useEffect(() => {
        axios
            .get(API + "expenses/fee-types/", { headers: authHeader() })
            .then((response) => setFeeTypes(response.data || []))
            .catch(() => toast.error(t("Could not load the fee types."), toastOptions));
    }, []);

    // Tell the page which rows are saved, so reopening the panel shows them.
    const publish = (next) => {
        setRows(withBlankRow(next));
        onRowsChange(
            product.id,
            next.filter((row) => row.id).map(({ id, product_setting_id, fee_type, tax_code }) => ({
                id, product_setting_id, fee_type, tax_code,
            }))
        );
    };

    const update = (key, patch) =>
        setRows((prev) => prev.map((row) => (row.key === key ? { ...row, ...patch, dirty: true } : row)));

    const save = (row) => {
        const body = {
            product_setting_id: product.id,
            fee_type: row.fee_type,
            tax_code: (row.tax_code || "").trim(),
        };
        const request = row.id
            ? axios.put(API + `company/product-tax-fee-codes/${row.id}/`, body, { headers: authHeader() })
            : axios.post(API + "company/product-tax-fee-codes/", body, { headers: authHeader() });
        request
            .then((response) => {
                const saved = toRow(response.data.data);
                // A save over an existing fee type returns that row: don't show it twice.
                publish(
                    rows
                        .filter((r) => r.key === row.key || r.id !== saved.id)
                        .map((r) => (r.key === row.key ? saved : r))
                );
                toast.success(t("Tax code updated successfully."), toastOptions);
            })
            .catch((error) => toast.error(errorMessage(error), toastOptions));
    };

    // x on a saved row deletes it; on a row being filled in, it clears it.
    const remove = (row) => {
        if (!row.id) {
            setRows((prev) => withBlankRow(prev.filter((r) => r.key !== row.key)));
            return;
        }
        axios
            .delete(API + `company/product-tax-fee-codes/${row.id}/`, { headers: authHeader() })
            .then(() => {
                publish(rows.filter((r) => r.key !== row.key));
                toast.success(t("Tax code removed."), toastOptions);
            })
            .catch((error) => toast.error(errorMessage(error), toastOptions));
    };

    return (
        <div style={panelStyle}>
            <div className="mb-1" style={gridStyle}>
                <div style={{ ...columns, ...headingStyle }}>{t("Fee Type")}</div>
                <div style={{ ...columns, ...headingStyle }}>{t("Tax Code")}</div>
                <div></div>
            </div>

            {rows.map((row) => {
                const complete = row.fee_type && (row.tax_code || "").trim();
                const touched = row.id || row.dirty;
                return (
                    <div key={row.key} className="mb-1" style={gridStyle}>
                        <div style={columns}>
                            <select
                                className="form-select form-select-sm"
                                style={controlStyle}
                                value={row.fee_type}
                                onChange={(e) => update(row.key, { fee_type: e.target.value })}
                            >
                                <option value="">{t("Select Fee Type")}</option>
                                {feeTypes.map((fee) => (
                                    <option key={fee.value} value={fee.value}>{fee.label}</option>
                                ))}
                            </select>
                        </div>
                        <div style={columns}>
                            <input
                                type="text"
                                className="form-control form-control-sm"
                                style={controlStyle}
                                maxLength={30}
                                value={row.tax_code}
                                onChange={(e) => update(row.key, { tax_code: e.target.value })}
                            />
                        </div>
                        <div className="text-nowrap d-flex" style={{ gap: 4 }}>
                            {row.dirty && (
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-success"
                                    style={buttonStyle}
                                    disabled={!complete}
                                    title={t("Save")}
                                    onClick={() => save(row)}
                                >
                                    &#10003;
                                </button>
                            )}
                            {touched && (
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    style={buttonStyle}
                                    title={row.id ? t("Delete") : t("Clear")}
                                    onClick={() => remove(row)}
                                >
                                    x
                                </button>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default FeeMapping;
